import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  TextField,
  Button,
  Grid,
  Typography,
  Box,
  Alert,
  MenuItem,
  IconButton,
  Divider
} from '@mui/material';
import { ArrowBack, Save, Cancel } from '@mui/icons-material';
import api from '../services/api';

const EditEmployee = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  const [formData, setFormData] = useState({
    personal_code: '',
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    nationality: 'LT',
    address_street: '',
    address_city: '',
    address_postal_code: '',
    address_country: 'LT',
    employment_type: 'temporary',
    sodra_code: '',
    tax_code: '',
    bank_account: '',
    bank_name: '',
    work_permit_number: '',
    work_permit_valid_until: '',
    residence_permit_number: '',
    residence_permit_valid_until: '',
    emergency_contact_name: '',
    emergency_contact_phone: '',
    notes: '',
    status: 'active'
  });

  useEffect(() => {
    fetchEmployee();
  }, [id]);

  const fetchEmployee = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/employees/${id}`);
      const employee = response.data;
      
      // Format dates for input fields
      if (employee.work_permit_valid_until) {
        employee.work_permit_valid_until = employee.work_permit_valid_until.split('T')[0];
      }
      if (employee.residence_permit_valid_until) {
        employee.residence_permit_valid_until = employee.residence_permit_valid_until.split('T')[0];
      }
      
      setFormData(employee);
      setError('');
    } catch (error) {
      console.error('Error fetching employee:', error);
      setError('Failed to load employee data');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      // Remove read-only fields before sending
      const updateData = { ...formData };
      delete updateData.id;
      delete updateData.date_of_birth;
      delete updateData.registration_date;
      delete updateData.updated_at;

      await api.put(`/employees/${id}`, updateData);
      setSuccess('Employee updated successfully!');
      
      // Redirect after 2 seconds
      setTimeout(() => {
        navigate(`/admin/employees/${id}`);
      }, 2000);
    } catch (error) {
      console.error('Error updating employee:', error);
      setError(error.response?.data?.error || 'Failed to update employee');
    }
  };

  if (loading) {
    return (
      <Container>
        <Typography>Loading employee data...</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
        <IconButton onClick={() => navigate(`/admin/employees/${id}`)}>
          <ArrowBack />
        </IconButton>
        <Typography variant="h4">
          Edit Employee: {formData.first_name} {formData.last_name}
        </Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      <Paper sx={{ p: 3 }}>
        <form onSubmit={handleSubmit}>
          {/* Personal Information */}
          <Typography variant="h6" gutterBottom>
            Personal Information
          </Typography>
          <Divider sx={{ mb: 3 }} />
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Personal Code"
                name="personal_code"
                value={formData.personal_code}
                disabled
                helperText="Cannot be modified"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="First Name"
                name="first_name"
                value={formData.first_name}
                onChange={handleChange}
                required
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Last Name"
                name="last_name"
                value={formData.last_name}
                onChange={handleChange}
                required
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Email"
                name="email"
                type="email"
                value={formData.email || ''}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Phone"
                name="phone"
                value={formData.phone || ''}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                select
                label="Nationality"
                name="nationality"
                value={formData.nationality || 'LT'}
                onChange={handleChange}
              >
                <MenuItem value="LT">Lithuania</MenuItem>
                <MenuItem value="PL">Poland</MenuItem>
                <MenuItem value="UA">Ukraine</MenuItem>
                <MenuItem value="BY">Belarus</MenuItem>
                <MenuItem value="LV">Latvia</MenuItem>
                <MenuItem value="EE">Estonia</MenuItem>
                <MenuItem value="OTHER">Other</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                select
                label="Status"
                name="status"
                value={formData.status || 'active'}
                onChange={handleChange}
              >
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
                <MenuItem value="terminated">Terminated</MenuItem>
              </TextField>
            </Grid>
          </Grid>

          {/* Address Information */}
          <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
            Address Information
          </Typography>
          <Divider sx={{ mb: 3 }} />
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Street Address"
                name="address_street"
                value={formData.address_street || ''}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="City"
                name="address_city"
                value={formData.address_city || ''}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Postal Code"
                name="address_postal_code"
                value={formData.address_postal_code || ''}
                onChange={handleChange}
              />
            </Grid>
          </Grid>

          {/* Employment Information */}
          <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
            Employment Information
          </Typography>
          <Divider sx={{ mb: 3 }} />
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                select
                label="Employment Type"
                name="employment_type"
                value={formData.employment_type || 'temporary'}
                onChange={handleChange}
              >
                <MenuItem value="temporary">Temporary</MenuItem>
                <MenuItem value="permanent">Permanent</MenuItem>
                <MenuItem value="seasonal">Seasonal</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="SODRA Code"
                name="sodra_code"
                value={formData.sodra_code || ''}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Tax Code"
                name="tax_code"
                value={formData.tax_code || ''}
                onChange={handleChange}
              />
            </Grid>
          </Grid>

          {/* Banking Information */}
          <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
            Banking Information
          </Typography>
          <Divider sx={{ mb: 3 }} />
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Bank Account (IBAN)"
                name="bank_account"
                value={formData.bank_account || ''}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Bank Name"
                name="bank_name"
                value={formData.bank_name || ''}
                onChange={handleChange}
              />
            </Grid>
          </Grid>

          {/* Work Permits (if applicable) */}
          <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
            Work Permits (Non-EU Citizens)
          </Typography>
          <Divider sx={{ mb: 3 }} />
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Work Permit Number"
                name="work_permit_number"
                value={formData.work_permit_number || ''}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Work Permit Valid Until"
                name="work_permit_valid_until"
                type="date"
                value={formData.work_permit_valid_until || ''}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Residence Permit Number"
                name="residence_permit_number"
                value={formData.residence_permit_number || ''}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Residence Permit Valid Until"
                name="residence_permit_valid_until"
                type="date"
                value={formData.residence_permit_valid_until || ''}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>

          {/* Emergency Contact */}
          <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
            Emergency Contact
          </Typography>
          <Divider sx={{ mb: 3 }} />
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Emergency Contact Name"
                name="emergency_contact_name"
                value={formData.emergency_contact_name || ''}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Emergency Contact Phone"
                name="emergency_contact_phone"
                value={formData.emergency_contact_phone || ''}
                onChange={handleChange}
              />
            </Grid>
          </Grid>

          {/* Notes */}
          <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
            Additional Notes
          </Typography>
          <Divider sx={{ mb: 3 }} />
          
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Notes"
                name="notes"
                value={formData.notes || ''}
                onChange={handleChange}
              />
            </Grid>
          </Grid>

          {/* Action Buttons */}
          <Box sx={{ mt: 4, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              startIcon={<Cancel />}
              onClick={() => navigate(`/admin/employees/${id}`)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              startIcon={<Save />}
            >
              Save Changes
            </Button>
          </Box>
        </form>
      </Paper>
    </Container>
  );
};

export default EditEmployee;
